'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useAtomValue, useAtom } from 'jotai';
import { ethers } from 'ethers';
import { usePrivy } from '@privy-io/react-auth';
import ModalConnect from '@/app/components/Modals/components/ModalConnect';
import { useTokenContext } from '@/app/context/TokensContext';
import BaseModal from '@/app/components/Modals/BaseModal';
import DelegateRadioButtons from './components/DelegateRadioButtons';
import ActionButton from './components/ActionButton';
import { hasTokensAtom, tokenInfoAtom } from '@/app/atoms/token';
import { UserData } from '@/app/types/userData';
import { userSettingsAtom } from '@/app/atoms/users';
import { openTransactionUrl } from '@/app/helpers/openTransactionUrl';
import { crank } from '@/app/helpers/crank';
import { UniversalAvatar } from '../UniversalAvatar';
import { userDataAtom } from '@/app/atoms/userData';

interface HeaderDelegationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: UserData;
  activeModalDelegate: `0x${string}` | undefined;
  setActiveModalDelegate: (delegate: `0x${string}` | '0x') => void;
}

const HeaderDelegationModal: React.FC<HeaderDelegationModalProps> = ({
  isOpen,
  onClose,
  setActiveModalDelegate,
}) => {
  const [newSettings] = useAtom(userSettingsAtom);
  const userData = useAtomValue(userDataAtom);
  const tokenInfo = useAtomValue(tokenInfoAtom);
  const hasTokens = useAtomValue(hasTokensAtom);
  const { authenticated } = usePrivy();
  const { tokenDelegate } = useTokenContext();
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [isPending, setIsPending] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [delegateAddress, setDelegateAddress] = useState<`0x${string}` | ''>('');
  const [hasDelegationStatement, setHasDelegationStatement] = useState<boolean>(false);

  const userHasDelegate = userData.delegateCount > 0;
  const usersDelegate = userHasDelegate
    ? (userData.delegatesAddress[0] as `0x${string}`)
    : '';

  useEffect(() => {
    if (selectedAction === 'manualDelegation' && delegateAddress !== '') {
      setActiveModalDelegate(delegateAddress as `0x${string}`);
    } else if (selectedAction === 'selfDelegate') {
      setActiveModalDelegate(userData.address as `0x${string}`);
    } else if (userHasDelegate) {
      setActiveModalDelegate(usersDelegate as `0x${string}`);
    }
  }, [
    setActiveModalDelegate,
    userData.address,
    userHasDelegate,
    usersDelegate,
    selectedAction,
    delegateAddress,
  ]);

  useEffect(() => {
    setHasDelegationStatement(
      newSettings?.delegation_statement !== undefined &&
        newSettings?.delegation_statement !== '',
    );
  }, [newSettings]);

  const handleButtonClick = async () => {
    setIsPending(true);
    setIsConfirming(false);
    setIsConfirmed(false);
    setError(null);
    try {
      const delegatee =
        selectedAction === 'manualDelegation' && delegateAddress !== ''
          ? (delegateAddress as `0x${string}`)
          : selectedAction === 'revoke'
            ? (ethers.ZeroAddress as `0x${string}`)
            : (userData.address as `0x${string}`);
      const result = await tokenDelegate({ delegatee });
      setTxHash(result.txHash);
      setIsConfirming(true);
      // Triggering the API data update
      crank();
      setIsConfirmed(true);
    } catch (error) {
      setError('An error occurred during delegation');
    } finally {
      setIsPending(false);
      setIsConfirming(false);
    }
  };

  const handleViewTx = () => {
    if (txHash) {
      openTransactionUrl(txHash);
    }
  };

  const getButtonText = () => {
    if (isPending) {
      if (selectedAction === 'revoke') return 'Revoking...';
      if (selectedAction === 'find' || selectedAction === 'ask') return 'Submitting...';
      return 'Delegating...';
    }
    if (isConfirming) return 'Processing...';
    if (isConfirmed) {
      if (txHash) return 'View on BlockExplorer';
      if (selectedAction === 'revoke') return 'Revoked';
      if (selectedAction === 'find' || selectedAction === 'ask') return 'Submitted';
      return 'Delegated';
    }
    if (error) return 'Error';
    return 'Submit';
  };

  useEffect(() => {
    if (error) {
      onClose();
    }
  }, [error, onClose]);

  const renderActionButton = () => (
    <ActionButton
      selectedAction={selectedAction}
      isPending={isPending}
      isConfirming={isConfirming}
      buttonTxt={getButtonText()}
      handleSubmit={handleButtonClick}
      handleButtonClick={handleButtonClick}
      handleViewTx={handleViewTx}
      userData={userData}
      delegateAddress={delegateAddress}
    />
  );

  const renderContent = () => {
    if (!authenticated) {
      return <ModalConnect />;
    }

    if (authenticated && !hasTokens) {
      return (
        <div className="text-center">
          <p className="mt-4 mb-6">
            {tokenInfo.symbol} ownership is what determines voting power. Once you have{' '}
            {tokenInfo.symbol} tokens, you can then delegate them to a wallet address to
            gain voting rights.
          </p>
          <Link
            href="/karratco/submit/delegationstatement"
            className="flex justify-center w-full bg-link text-neutral-50 hover:bg-hover rounded py-2 mt-4"
          >
            Ask for Delegations
          </Link>
        </div>
      );
    } else {
      return (
        <>
          {!txHash || !isConfirmed ? (
            <>
              <DelegateRadioButtons
                selectedAction={selectedAction}
                setSelectedAction={setSelectedAction}
                disabled={isPending || isConfirming}
                options={generateOptions()}
              />
              <div>{getUsersDelegateInfoText()}</div>
            </>
          ) : (
            txHash &&
            isConfirmed && <div className="my-6"> Your transaction is complete. </div>
          )}
          {selectedAction === 'manualDelegation' && (
            <>
              <label className="flex justify-start min-w-full mt-1">
                Delegate&apos;s Address
              </label>
              <input
                type="text"
                placeholder="0xAddress"
                value={delegateAddress}
                onChange={(e) => setDelegateAddress(e.target.value as `0x${string}`)}
                className="mt-1 p-2 border border-neutral-700 rounded w-full bg-neutral-700 placeholder-neutral-400 focus:outline-none focus-visible:ring-neutral-700 focus-visible:ring-0"
              />
            </>
          )}
          {renderActionButton()}
        </>
      );
    }
  };

  const generateOptions = () => {
    const options = ['find', 'ask'];
    if (userData.delegateCount === 0) {
      options.push('manualDelegation');
      options.unshift('selfDelegate');
    } else {
      options.unshift('revoke');
    }
    if (hasDelegationStatement) {
      options[options.indexOf('ask')] = 'view';
    }
    return options;
  };

  const getUsersDelegateInfoText = () => {
    if (selectedAction === 'revoke') {
      return isConfirmed ? (
        'Revoked voting rights from'
      ) : (
        <div className="flex flex-col pt-4 items-center min-w-full">
          <p className="pb-2">Revoking voting rights from</p>
          <div className="flex items-center mb-4">
            <UniversalAvatar
              address={userData.delegatesAddress[0] as `0x${string}`}
              width={20}
              height={20}
              size={20}
            />
          </div>
        </div>
      );
    }
    if (selectedAction === 'selfDelegate') {
      return isConfirmed ? (
        'Delegated voting rights to'
      ) : (
        <div className="flex flex-col pt-4 items-center min-w-full">
          <p className="pb-2">Delegating voting rights to</p>
          <div className="flex items-center mb-4">
            <UniversalAvatar
              address={userData.address as `0x${string}`}
              width={20}
              height={20}
              size={20}
            />
          </div>
        </div>
      );
    }
    if (selectedAction === 'manualDelegation') {
      return isConfirmed ? (
        'Delegated voting rights to '
      ) : (
        <>
          <p className="pb-2">Delegating voting rights worth</p>
          <p>{`${tokenInfo.balance} ${tokenInfo.symbol}`} to </p>
        </>
      );
    }
    return null;
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={authenticated ? 'Manage Delegation' : 'Connect Wallet'}
      disableClose={isPending || isConfirming}
    >
      {renderContent()}
    </BaseModal>
  );
};

export default HeaderDelegationModal;
